import { API_ROUTES, VERIFICATION_STATUS } from '../../constants';
import { useS3Uploader } from '../useS3Uploader';
import { useEffect, useState } from 'react';
import { axiosInstance } from '../../services';
import { useQuery } from '@tanstack/react-query';

export const fetchDoctorVerificationDetails = async (doctorId: string) => {
  const { data } = await axiosInstance.get(
    `${API_ROUTES.getDoctorVerificationDetails}?practiseAdminId=${doctorId}`
  );
  return data;
};

export const useDoctorVerificationDetails = (
  doctorId: string,
  doctorVerificationSelectedTab: number
) => {
  const { generateSignedUrl } = useS3Uploader();

  /* -------------------- QUERY -------------------- */
  const { data, isLoading, isError } = useQuery({
    queryKey: ['doctor-verification-details', doctorId, doctorVerificationSelectedTab],
    queryFn: () => fetchDoctorVerificationDetails(doctorId as string),
    enabled: Boolean(doctorId),
    refetchOnMount: 'always'
  });

  /* -------------------- DERIVED DATA -------------------- */
  const selectedDoctor = data?.practiseBasicDetails && data?.practiseBasicDetails[0];

  /* -------------------- STATUS HELPERS -------------------- */
  const overallStatus = selectedDoctor?.verificationStatus;

  const isRejected = overallStatus === VERIFICATION_STATUS.REJECTED;
  const isUpdatedRequested = overallStatus === VERIFICATION_STATUS.UPDATE_REQUESTED;
  const isVerified = overallStatus === VERIFICATION_STATUS.VERIFIED;

  const isUpdateOrVerificationBlocked = isRejected || isVerified;

  const isPending = (status?: string) => status === VERIFICATION_STATUS.PENDING;

  const professionalDetailsStatus = selectedDoctor?.practiseMedicalDocumentRelation?.status;

  const medicalDetailsStatus = selectedDoctor?.practiseIdDocumentsRelation?.status;

  const livePictureStatus = selectedDoctor?.practiseAdminLivePic?.status;

  const orgDetailStatus = selectedDoctor?.practiseDemographicsRelation?.status;

  const isProfessionalUpdateInProgresss = !isPending(professionalDetailsStatus);
  const isMedicalUpdateInProgresss = !isPending(medicalDetailsStatus);
  const isLivePictureUpdateInProgresss = !isPending(livePictureStatus);
  const isOrgUpdateInProgresss = !isPending(orgDetailStatus);

  /* -------------------- SIGNED URLS -------------------- */
  const [signedUrls, setSignedUrls] = useState({
    professionalDetailsUrl: '',
    medicalDetailsUrl: '',
    livePictureUrl: '',
    organisationalDetailsUrl: ''
  });

  useEffect(() => {
    const fetchSignedUrls = async () => {
      if (!selectedDoctor) return;

      const [professionalDetailsUrl, medicalDetailsUrl, livePictureUrl, organisationalDetailsUrl] =
        await Promise.all([
          selectedDoctor?.practiseMedicalDocumentRelation?.medicalDocumentContent
            ? generateSignedUrl(
                selectedDoctor.practiseMedicalDocumentRelation.medicalDocumentContent
              )
            : '',
          selectedDoctor?.practiseIdDocumentsRelation?.idDocumentContent
            ? generateSignedUrl(selectedDoctor.practiseIdDocumentsRelation.idDocumentContent)
            : '',
          selectedDoctor?.practiseAdminLivePic?.imageContent
            ? generateSignedUrl(selectedDoctor.practiseAdminLivePic.imageContent)
            : '',
          selectedDoctor?.practiseDemographicsRelation?.practiseFilesRelation?.fileContent
            ? generateSignedUrl(
                selectedDoctor.practiseDemographicsRelation.practiseFilesRelation.fileContent
              )
            : ''
        ]);

      setSignedUrls({
        professionalDetailsUrl,
        medicalDetailsUrl,
        livePictureUrl,
        organisationalDetailsUrl
      });
    };

    fetchSignedUrls();
  }, [selectedDoctor, generateSignedUrl]);

  /* -------------------- RETURN -------------------- */
  return {
    data,
    isLoading,
    isError,

    selectedDoctor,
    overallStatus,

    isRejected,
    isUpdatedRequested,
    isVerified,
    isUpdateOrVerificationBlocked,

    isProfessionalUpdateInProgresss,
    isMedicalUpdateInProgresss,
    isLivePictureUpdateInProgresss,
    isOrgUpdateInProgresss,

    professionalDetailsStatus,
    medicalDetailsStatus,
    livePictureStatus,
    orgDetailStatus,

    signedUrls
  };
};
